import {
  IArnoMigration,
  IArnoMigrationIssue,
} from '../../hooks/reactQuerys/ArnoAdminQueries';

/**
 * Migrationsübersicht eines kopierten Baums (#1245).
 *
 * Reine Funktionen, weil hier die Aussagen stehen, die stimmen müssen: wie viele
 * Entscheidungen offen sind, wie sie heißen, und welche sich im Editor direkt
 * auflösen lassen. Erfundene Quoten gibt es nicht — gezählt wird, was im
 * gespeicherten Entwurf steht.
 */

/**
 * Klartext der Gründe. Der Code kommt aus dem Modell, der Satz aus dem Notariat.
 *
 * Die Überschrift sagt, **was fehlt**; der Satz darunter sagt, **was das für die Urkunde
 * heißt**. »UNSUPPORTED_BRANCH« als Überschrift hilft niemandem, der eine Urkunde schreibt.
 */
export const REASON_LABEL: Record<string, string> = {
  UNMAPPED_VARIABLE: 'Felder ohne Entsprechung in DiNo',
  UNRESOLVED_BLOCK_REFERENCE: 'Textbausteine, die nicht gefunden wurden',
  LEGACY_REGISTER_LOGIC: 'Alte Steuerlogik aus ArnoTop',
  UNSUPPORTED_WORD_FIELD: 'Word-Felder, die sich nicht übertragen ließen',
  UNSUPPORTED_FORMATTING: 'Formatierung, die sich nicht übertragen ließ',
  AMBIGUOUS_CONDITION: 'Bedingungen, die nicht eindeutig sind',
  UNSUPPORTED_NODE: 'Sonstige offene Stellen',
};

/** Ein Satz je Grund: was bedeutet das an dieser Stelle im Dokument? */
export const REASON_EXPLANATION: Record<string, string> = {
  UNMAPPED_VARIABLE:
    'Das alte Feld hat in DiNo noch keine Entsprechung. Wählen Sie ein Datenfeld aus, oder lassen Sie die Stelle offen — dann steht sie sichtbar im Entwurf.',
  UNRESOLVED_BLOCK_REFERENCE:
    'Der Textbaustein, auf den hier verwiesen wird, wurde nicht mitkopiert. Sie können ihn zuordnen oder den Verweis entfernen.',
  LEGACY_REGISTER_LOGIC:
    'ArnoTop hat hier mit einer internen Merkgröße gesteuert. In DiNo tritt an ihre Stelle eine Frage.',
  UNSUPPORTED_WORD_FIELD:
    'Diese alte Word-Bedingung konnte nicht sicher übernommen werden.',
  UNSUPPORTED_FORMATTING:
    'Diese Formatierung hat in DiNo keine Entsprechung. Der Text ist da, nur seine Auszeichnung nicht.',
  AMBIGUOUS_CONDITION:
    'Die Bedingung ließe sich auf mehrere Arten verstehen. Geraten wird nicht — bitte einmal entscheiden.',
  UNSUPPORTED_NODE:
    'Diese Stelle konnte beim Kopieren nicht automatisch übernommen werden.',
};

export const reasonLabel = (code: string): string => REASON_LABEL[code] ?? code;

/** Der erklärende Satz — oder ein allgemeiner, wenn der Grund neu ist. */
export const reasonExplanation = (code: string): string =>
  REASON_EXPLANATION[code] ??
  'Diese Stelle konnte beim Kopieren nicht automatisch übernommen werden.';

/** Gruppen in der Reihenfolge, in der sie angezeigt werden: die größte zuerst. */
export const orderedGroups = (migration?: IArnoMigration) =>
  [...(migration?.groups ?? [])].sort((a, b) =>
    b.count !== a.count
      ? b.count - a.count
      : a.reasonCode.localeCompare(b.reasonCode)
  );

/** Was an einer offenen Word-Bedingung steht — für die technischen Details. */
export interface IWordIfDetail {
  ifClass?: string;
  fieldCode?: string;
  operator?: string;
  value?: string;
  variable?: string;
  onlyMappingMissing?: boolean;
}

/** Klartext der Sicherheitsklassen einer Word-Bedingung. Messwerte, keine Prozente. */
export const IF_CLASS_LABEL: Record<string, string> = {
  SAFE_CONVERTIBLE: 'sicher übernehmbar',
  STRUCTURALLY_CONVERTIBLE_BUT_UNMAPPED: 'nur die Zuordnung fehlt',
  AMBIGUOUS_VALUE_SEMANTICS: 'linke Seite ist kein Feld',
  NESTED_COMPLEX: 'verschachtelte Bedingung',
  UNSUPPORTED_FIELD: 'enthält ein anderes Word-Feld',
  UNSUPPORTED_OPERATOR: 'Vergleich ohne native Entsprechung',
  UNSUPPORTED_BRANCH: 'Zweig enthält mehr als Text',
  PARSE_ERROR: 'Feldcode nicht lesbar',
};

export const ifClassLabel = (code?: string): string =>
  code ? (IF_CLASS_LABEL[code] ?? code) : '';

/** Die Angaben einer offenen Word-Bedingung, falls es eine ist. */
export const wordIfDetail = (
  issue: IArnoMigrationIssue
): IWordIfDetail | null => {
  const detail = (issue.detail ?? {}) as IWordIfDetail;
  return detail.ifClass ? detail : null;
};

/**
 * Fehlt dieser Stelle nur noch eine Zuordnung?
 *
 * Dann genügt die Wahl eines DiNo-Feldes, und die Bedingung wird beim nächsten
 * Durchlauf automatisch übernommen — das ist der Satz, den die Oberfläche sagen
 * darf, ohne etwas zu versprechen, das sie nicht hält.
 */
export const onlyMappingMissing = (issue: IArnoMigrationIssue): boolean =>
  Boolean((issue.detail as IWordIfDetail)?.onlyMappingMissing);

/**
 * Lässt sich diese Stelle im Editor selbst auflösen?
 *
 * Nur eine ungemappte Variable: dort genügt die Wahl eines DiNo-Feldes. Alles
 * andere (Registerlogik, Word-Felder, Verweise) ist eine fachliche Entscheidung,
 * die kein Auswahlfeld abnehmen kann.
 */
export const isResolvable = (issue: IArnoMigrationIssue): boolean =>
  issue.reasonCode === 'UNMAPPED_VARIABLE' &&
  typeof issue.detail?.variable === 'string' &&
  Boolean(issue.detail.variable);

/** Der Legacy-Feldname einer auflösbaren Stelle. */
export const variableOf = (issue: IArnoMigrationIssue): string =>
  typeof issue.detail?.variable === 'string' ? issue.detail.variable : '';

/** Die auflösbaren Stellen, je Legacy-Feld einmal — dieselbe Entscheidung gilt für alle. */
export const resolvableVariables = (
  migration?: IArnoMigration
): { variable: string; places: number }[] => {
  const counts = new Map<string, number>();
  // ArnoTop-Beteiligtenplätze (B1, ANWESEND_B1 …) sind keine gewöhnlichen offenen Felder: welcher
  // DiNo-Beteiligte B1 ist, lässt sich nicht belegen. Sie werden nicht zum Zuordnen angeboten
  // (#1245, Teil 41) — das Backend weist es ohnehin ab.
  const plaetze = new Set(migration?.participantSlots ?? []);
  for (const issue of migration?.issues ?? []) {
    if (!isResolvable(issue) || plaetze.has(variableOf(issue))) continue;
    const variable = variableOf(issue);
    counts.set(variable, (counts.get(variable) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([variable, places]) => ({ variable, places }))
    .sort((a, b) =>
      b.places !== a.places
        ? b.places - a.places
        : a.variable.localeCompare(b.variable)
    );
};

/** „90 Bausteine · 183 Entscheidungen offen" — die Kopfzeile der Übersicht. */
export const migrationSummary = (migration?: IArnoMigration): string => {
  if (!migration) return '';
  const decisions = migration.open === 1 ? 'Entscheidung' : 'Entscheidungen';
  return `${migration.blocks} Bausteine · ${migration.open} ${decisions} offen`;
};

/**
 * Was beim Kopieren mit der Formatierung geschehen ist — in einem Satz.
 *
 * Die Zähler gab es längst, gezeigt wurden sie nie. Das betrifft vor allem eine bewusste
 * Entscheidung: Gliederungsnummern an Überschriften reisen **nicht** mit, weil die Word-Vorlage
 * des Notariats seine Überschriften selbst nummeriert (Spec §16). Gemessen über alle 36
 * importierten Bäume sind das 510 Stellen. Eine Entscheidung, die niemand sieht, ist von einem
 * stillen Verlust nicht zu unterscheiden.
 */
export const formattingSummary = (
  rich: Record<string, number> | undefined
): string => {
  if (!rich) {
    return '';
  }
  const uebernommen: string[] = [];
  const absaetze = rich['layout:lineSpacing'] ?? 0;
  if (absaetze) {
    uebernommen.push(
      `${absaetze} ${absaetze === 1 ? 'Absatz' : 'Absätze'} mit eigenem Zeilenabstand`
    );
  }
  if (rich.carriedLists) {
    uebernommen.push(`${rich.carriedLists} Listenangaben`);
  }
  if (rich.directRunFormats) {
    uebernommen.push(`${rich.directRunFormats} Schriftauszeichnungen`);
  }
  const saetze: string[] = [];
  if (uebernommen.length > 0) {
    saetze.push(`Formatierung übernommen: ${uebernommen.join(', ')}.`);
  }
  const gliederung = rich.outlineNumberingDropped ?? 0;
  if (gliederung) {
    saetze.push(
      `${gliederung} ${gliederung === 1 ? 'Überschrift nummeriert' : 'Überschriften nummerieren'} ` +
        'jetzt die Word-Vorlage statt ArnoTop.'
    );
  }
  const offen = rich.formattingOpen ?? 0;
  if (offen) {
    saetze.push(
      `${offen} ${offen === 1 ? 'Stelle' : 'Stellen'} mit Formatierung, die sich nicht ` +
        `sicher übersetzen ließ, ${offen === 1 ? 'bleibt' : 'bleiben'} offen.`
    );
  }
  return saetze.join(' ');
};

/**
 * Ein Word-Feldcode, wie ein Mensch ihn lesen kann.
 *
 * Im Altbestand steht der Feldcode mit Words Steuerzeichen (0x13 Feldanfang, 0x14 Trenner,
 * 0x15 Feldende) — in der Oberfläche erschienen sie als leere Kästchen. Und hinter jedem Trenner
 * steht das **zuletzt berechnete Ergebnis** des Feldes: bei `DOCVARIABLE NN2` war das der Name
 * einer echten Person aus einem alten ArnoTop-Vorgang (Sichtprüfung Teil 40). Diese Ergebnisse
 * gehören nicht in die Anzeige; sie werden entfernt, verschachtelte Felder erscheinen als `{ … }`.
 */
export const readableFieldCode = (code: string): string => {
  let text = code ?? '';
  // Innerste Felder zuerst: { Code 0x14 Ergebnis 0x15 } → { Code }
  let vorher = '';
  while (vorher !== text) {
    vorher = text;
    text = text.replace(
      /\u0013([^\u0013\u0014\u0015]*)\u0014[^\u0013\u0014\u0015]*\u0015/g,
      '{$1}'
    );
    text = text.replace(/\u0013([^\u0013\u0014\u0015]*)\u0015/g, '{$1}');
  }
  return text
    .replace(/[\u0000-\u001f]/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\{\s*/g, '{ ')
    .replace(/\s*\}/g, ' }')
    .trim();
};
